import React, { useRef, useState, useEffect, useMemo } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Sparkles } from 'lucide-react';
// Aurora background component
const AuroraBackground = ({
  isHovered
}: {
  isHovered: boolean;
}) => {
  return <div className="absolute inset-0 rounded-lg" data-id="element-1010">
      {/* Single static gradient that glows */}
      <motion.div className="absolute inset-0 rounded-lg" style={{
      background: `
            radial-gradient(ellipse at center, 
              hsl(140 40% 50% / 0.3) 0%, 
              hsl(225 40% 55% / 0.2) 30%, 
              transparent 60%)
          `,
      filter: "blur(25px)"
    }} animate={{
      opacity: isHovered ? 0.8 : 0.6,
      scale: isHovered ? 1.05 : 1
    }} transition={{
      duration: 0.3,
      ease: "easeInOut"
    }} data-id="element-1011" />
    </div>;
};

// Typewriter effect component
const TypewriterPlaceholder = ({
  isActive
}: {
  isActive: boolean;
}) => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const examples = useMemo(() => ["Reply to Sarah's showing request", "Draft follow-up for open house leads", "Clear my morning emails", "Schedule showings for the Johnson property", "Send listing update to all buyers", "Thank yesterday's showing agents", "Check on pending offers"], []);
  useEffect(() => {
    if (!isActive) return;
    const currentExample = examples[currentIndex];
    const typeSpeed = isDeleting ? 20 : 40;
    const pauseTime = isDeleting ? 100 : 2000;
    const timer = setTimeout(() => {
      if (!isDeleting && currentText === currentExample) {
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && currentText === '') {
        setIsDeleting(false);
        setCurrentIndex(prev => (prev + 1) % examples.length);
      } else {
        setCurrentText(prev => isDeleting ? prev.slice(0, -1) : currentExample.slice(0, prev.length + 1));
      }
    }, typeSpeed);
    return () => clearTimeout(timer);
  }, [currentText, currentIndex, isDeleting, isActive, examples]);

  // Cursor blink effect
  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorTimer);
  }, []);
  return <span className="text-slate-600" data-id="element-1012">
      {currentText}
      <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity`} data-id="element-1013">|</span>
    </span>;
};
export function FloatingCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Proximity detection and mobile behavior
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // On mobile, show component when scrolling near bottom or touching lower area
      const handleScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY;
        const windowHeight = window.innerHeight;
        const distanceFromBottom = scrollHeight - (scrollTop + windowHeight);
        if (distanceFromBottom < 400) {
          setIsVisible(true);
        }
      };
      const handleTouchStart = (e: TouchEvent) => {
        const touch = e.touches[0];
        const windowHeight = window.innerHeight;
        const triggerZone = windowHeight - 150; // 150px from bottom on mobile

        if (touch.clientY >= triggerZone) {
          setIsVisible(true);
        }
      };
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('touchstart', handleTouchStart);

      // Show hint on page load for mobile
      setTimeout(() => setIsVisible(true), 1000);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('touchstart', handleTouchStart);
      };
    } else {
      // Desktop behavior
      const handleMouseMove = (e: MouseEvent) => {
        const windowHeight = window.innerHeight;
        const triggerZone = windowHeight - 200; // 200px from bottom

        if (e.clientY >= triggerZone || isHovered) {
          setIsVisible(true);
        } else if (!isFocused) {
          // Immediate hide - no delay
          setIsVisible(false);
        }
      };
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [isFocused]);

  // Keyboard shortcut (Cmd/Ctrl + K to focus)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsVisible(true);
        // Focus input after animation
        setTimeout(() => {
          const input = containerRef.current?.querySelector('input');
          if (input) input.focus();
        }, 300);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    window.location.href = '/waitlist';
  };
  return <motion.div ref={containerRef} className="fixed bottom-6 inset-x-4 mx-auto max-w-3xl z-50" initial={{
    y: "60%"
  }} animate={{
    y: isVisible ? "0%" : "60%"
  }} transition={{
    type: "spring",
    stiffness: 300,
    damping: 30,
    mass: 0.8
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => {
    setIsHovered(false);
    if (!isFocused) {
      setIsVisible(false); // Immediate hide when leaving
    }
  }} data-id="element-1014">
      <div className="relative" data-id="element-1015">
        {/* Aurora background */}
        <div className="absolute -inset-3 -z-10" data-id="element-1016">
          <AuroraBackground isHovered={isHovered} data-id="element-1017" />
        </div>
        
        {/* Main CTA Form */}
        <motion.form onSubmit={handleSubmit} className="relative z-10 pointer-events-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 rounded-lg border border-white/20 bg-white/95 backdrop-blur-md dark:bg-slate-900/90 shadow-2xl w-full min-h-[48px] aurora-glow" animate={{
        scale: isFocused ? 1.02 : 1,
        boxShadow: isFocused ? "0 0 0 3px hsl(140 30% 50% / 0.4), 0 0 40px hsl(140 30% 50% / 0.3), 0 0 80px hsl(140 30% 50% / 0.2), 0 10px 40px rgba(0, 0, 0, 0.1)" : isHovered ? "0 0 30px hsl(140 30% 50% / 0.25), 0 0 60px hsl(140 30% 50% / 0.15), 0 8px 30px rgba(0, 0, 0, 0.12)" : "0 0 20px hsl(140 30% 50% / 0.2), 0 0 40px hsl(140 30% 50% / 0.1), 0 4px 20px rgba(0, 0, 0, 0.1)"
      }} transition={{
        duration: 0.2
      }} role="search" aria-label="Ask Zen AI assistant" data-id="element-1018">
          <div className="flex-1 relative flex items-center" data-id="element-1019">
            <Input className="w-full pr-2 rounded-lg border-0 bg-transparent placeholder:text-transparent focus:placeholder:text-slate-600 transition-all text-base sm:text-sm h-10" placeholder="Talk to Zen…" value={inputValue} onChange={e => setInputValue(e.target.value)} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" aria-label="Enter your request for Zen AI assistant" aria-describedby="zen-instructions" data-id="element-1020" />
            {/* Typewriter placeholder overlay */}
            {!isFocused && inputValue === '' && <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center h-full" data-id="element-1021">
                <TypewriterPlaceholder isActive={!isFocused && inputValue === ''} data-id="element-1022" />
              </div>}
          </div>
          
          <motion.button type="submit" aria-label="Submit prompt" className="relative ml-2 text-primary hover:text-primary/80 transition-colors flex-shrink-0 flex items-center justify-center" animate={{
          rotate: inputValue ? [0, 15, -15, 0] : 0,
          scale: inputValue ? [1, 1.1, 1] : 1
        }} transition={{
          duration: 0.3
        }} whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.95
        }} data-id="element-1023">
            <Sparkles className="w-5 h-5" data-id="element-1024" />
          </motion.button>
        </motion.form>
        
        {/* Hidden instructions for screen readers */}
        <div id="zen-instructions" className="sr-only" data-id="element-1025">
          AI assistant for real estate agents. Examples: reply to leads, draft emails, schedule showings. Use Cmd+K or Ctrl+K to quickly access this input.
        </div>
      </div>
    </motion.div>;
}